import React, { useCallback, useEffect, useState } from 'react';
import { StatusDialog } from '../../../components/custom-dialog';
import {
  Box,
  Button,
  FormControl,
  List,
  ListItem,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { API_ROUTES, ROLE_IDS } from '../../../utils/constant';
import HttpService from '../../../hooks/Https-services';
import { Comment, getBackgroundColor } from '../GameTableRow';
import CommentDataTable from './CommentDataTable';
import CommentDataList from './CommentDataList';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Define TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  activeTab: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, activeTab, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== activeTab}
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      {...other}
    >
      {value === activeTab && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

export const getRoleName = (roleId: number) => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
      return 'Admin';
    case ROLE_IDS.MODERATOR:
      return 'Moderator';
    case ROLE_IDS.TAGGER:
      return 'Tagger';
    case ROLE_IDS.SUPER_ADMIN:
      return 'Super Admin';
    case ROLE_IDS.TECHLEAD:
      return 'Techlead';
    default:
      return 'Unknown';
  }
};

function CommentBox(props: {
  data: any;
  gameData: any;
  isGame?: boolean;
  comment: any;
  commentText: any;
  setCommentText: any;
  addComment: any;
  isDelay?: boolean;
}) {
  const {
    data,
    isGame = false,
    comment,
    commentText,
    setCommentText,
    addComment,
    gameData,
    isDelay = false,
  } = props;

  // Add safe access to data properties

  const dataId = data?.id || '';

  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add state for tabs and delay comment
  const [activeTab, setActiveTab] = useState<'comment' | 'delay'>('comment');
  const [delayMinutes, setDelayMinutes] = useState(10);
  const [delayCommentText, setDelayCommentText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (comment.value && isDelay) {
      setActiveTab('delay');
    }
  }, [comment.value, isDelay]);

  const GetComments = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!comment.value) {
        setIsLoading(false);
        return;
      }

      if (isGame && data?.gameId) {
        // Fetch both regular comments and delayed comments in parallel
        const [commentsRes, delayCommentsRes] = await Promise.allSettled([
          HttpService.get(API_ROUTES.getComments.replace(':id', data.gameId)),
          HttpService.get(
            API_ROUTES.getDealyComments + `?type=DELAYED&gameId=${data.gameId}`
          ),
        ]);

        // Handle API responses
        const regularComments =
          commentsRes.status === 'fulfilled'
            ? commentsRes.value.data.data || []
            : [];

        const delayedComments =
          delayCommentsRes.status === 'fulfilled'
            ? delayCommentsRes.value.data.data || []
            : [];

        // Log any errors
        if (commentsRes.status === 'rejected') {
          console.error('Error fetching regular comments:', commentsRes.reason);
        }

        if (delayCommentsRes.status === 'rejected') {
          console.error(
            'Error fetching delayed comments:',
            delayCommentsRes.reason
          );
        }

        // Merge with any existing comments from props
        setComments([
          ...(data.comment || []),
          ...regularComments,
          ...delayedComments,
        ]);
      } else if (!isGame) {
        // For non-game, just use the comments from props
        setComments([...(data?.comment || [])]);
      }
    } catch (error) {
      console.error('Unexpected error in GetComments:', error);
      // Fallback: set comments from props even if something unexpected fails
      setComments([...(data?.comment || [])]);
    } finally {
      setIsLoading(false);
    }
  }, [comment.value, data?.comment, data?.gameId, isGame]);
  useEffect(() => {
    GetComments();
    return () => {
      setComments([]);
    };
  }, [GetComments]);

  // Handler functions for delay comment
  const handleIncrement = () => {
    setDelayMinutes((prev) => Math.min(prev + 10, 60));
  };

  const handleDecrement = () => {
    setDelayMinutes((prev) => Math.max(prev - 10, 10));
  };

  // Handle adding comments based on active tab
  const handleAddComment = async () => {
    if (dataId) {
      if (activeTab === 'comment') {
        addComment(dataId);
      } else {
        // For delay comments

        if (!delayCommentText.trim()) {
          console.warn('Comment cannot be empty.');
          return;
        }

        try {
          const commentData = {
            gameId: data.gameId,
            text: delayCommentText,
            timeDelay: moment.duration(delayMinutes, 'minutes').asSeconds(),
            type: 'DELAYED',
          };
          await HttpService.post(`${API_ROUTES.getDealyComments}`, commentData);

          setCommentText('');
          setDelayCommentText('');
          GetComments();
          setDelayMinutes(10);
          toast.success('Comment Add Successfully', {
            position: 'bottom-right',
          });
        } catch (error: any) {
          console.error('Error updating comments:', error);
          setCommentText('');
        }
        // Add your actual delay comment logic here
      }
    }
  };

  return (
    <StatusDialog
      open={comment.value}
      onClose={() => {
        comment.onFalse();
        setCommentText('');
        setDelayCommentText('');
        setDelayMinutes(10);
        setActiveTab('comment');
        isDelay && navigate(`/games?gameId=${data.gameId}`);
      }}
      fullWidth={true}
      maxWidth={!isGame ? 'xl' : 'lg'}
      title="Add Comment"
      content={
        <Box sx={{ display: 'flex', gap: 1, width: 1 }}>
          {!isGame && (
            <Box sx={{ flexGrow: 1, width: 0.5 }}>
              <Typography variant="h6">Game Comments</Typography>
              <CommentDataTable data={gameData} />
            </Box>
          )}

          <Box sx={{ flexGrow: 1, width: 0.5 }}>
            {' '}
            {!isGame && <Typography variant="h6">Period Comments</Typography>}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: 'row',
                alignItems: 'end',
                marginTop: '10px',
              }}
            >
              {!isGame && (
                <FormControl sx={{ width: '100%' }}>
                  <TextField
                    id="comment"
                    label="Comment"
                    variant="outlined"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    multiline
                    rows={3}
                    sx={{ width: '100%' }}
                  />
                </FormControl>
              )}

              {isGame && (
                <Box sx={{ width: '100%' }}>
                  {/* MUI Tabs */}
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Comment" value="comment" />
                    <Tab label="Delay Comment" value="delay" />
                  </Tabs>

                  {/* Tab Content */}
                  <CustomTabPanel value="comment" activeTab={activeTab}>
                    <FormControl sx={{ width: '100%' }}>
                      <TextField
                        id="comment"
                        label="Comment"
                        variant="outlined"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        multiline
                        rows={3}
                        sx={{ width: '100%' }}
                      />
                    </FormControl>
                  </CustomTabPanel>

                  <CustomTabPanel value="delay" activeTab={activeTab}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <FormControl sx={{ width: '100%' }}>
                        <TextField
                          id="delay-comment"
                          label="Delay Comment"
                          variant="outlined"
                          value={delayCommentText}
                          onChange={(e) => setDelayCommentText(e.target.value)}
                          multiline
                          rows={3}
                          sx={{ width: '100%' }}
                        />
                      </FormControl>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start', // Changed to left-align
                          mt: 1, // Add some top margin
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleDecrement}
                          disabled={delayMinutes <= 10}
                          sx={{ minWidth: '40px' }}
                        >
                          -10
                        </Button>

                        <Typography
                          variant="h6"
                          sx={{
                            mx: 2,
                            minWidth: '80px',
                            textAlign: 'center',
                          }}
                        >
                          {delayMinutes} min
                        </Typography>

                        <Button
                          variant="outlined"
                          onClick={handleIncrement}
                          disabled={delayMinutes >= 60}
                          sx={{ minWidth: '40px' }}
                        >
                          +10
                        </Button>
                      </Box>
                    </Box>
                  </CustomTabPanel>
                </Box>
              )}
              <Button
                variant="contained"
                color="success"
                sx={{
                  height: '40px',
                  whiteSpace: 'nowrap',
                  marginLeft: '10px',
                }}
                onClick={handleAddComment}
              >
                Add
              </Button>
            </Box>
            {isLoading ? (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2 }}
              >
                <Skeleton sx={{ py: 4 }} variant="rounded" />
                <Skeleton sx={{ py: 4 }} variant="rounded" />
                <Skeleton sx={{ py: 4 }} variant="rounded" />
              </Box>
            ) : (
              <CommentDataList comments={comments} />
            )}
          </Box>
        </Box>
      }
    />
  );
}

export default CommentBox;
